import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Link } from "react-router-dom"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useSidebar,
} from "ksc-portal-common-components/shadcn"
import { api } from "common/utils"
import { KR_THEME } from "ksc-portal-shareable-configs/dist/webpack/webpack-foundation/constants"
import { toast } from "sonner"
import { Menu } from "lucide-react"
import theme from "../../utils/theme"
import { modulesReady } from "../UserSettings/component"
import EntityDropdown from "./entityDropdown"
import DynamicTitle from "./DynamicTitle"
import { AvatarComponent } from "./Avatar"

const NavbarComponent = ({
  loggedIn,
  className,
  firstName,
  lastName,
  NavbarElements,
  entity_id,
  entities,
  idTokenEntityId,
  history,
  setEntities,
  setEntity,
  openSidebar,
  closeSidebar,
  isSidebarOpen,
  role,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { toggleSidebar: toggleShadcnSidebar, state } = useSidebar()

  const handleEntityChange = useCallback(
    ({ target: { value } }) => {
      const {
        location: { pathname, search },
      } = history
      const searchQuery = new URLSearchParams(search)
      searchQuery.set("entity_id", value)
      history.push({
        pathname,
        search: searchQuery.toString(),
      })
      setEntity(value)
    },
    [history, setEntity]
  )

  const updateEntities = useCallback(() => {
    api.invoke.iam
      .listEntities_v1()
      .then((res) => {
        setEntities(
          res.map(({ name, id }) => ({
            label: name,
            value: id,
          }))
        )
      })
      .catch(() => toast.error("Could not retrieve entities"))
  }, [setEntities])

  const loadIAM = useCallback(() => {
    const {
      location: { search },
    } = history
    const searchParams = new URLSearchParams(search)
    const entityId = searchParams.get("entity_id") || idTokenEntityId

    return api
      .load("iam")
      .then(() => {
        setEntity(entityId)
        if (api.check.iam.listEntities_v1()) updateEntities()
      })
      .catch(() => setEntity(entityId))
  }, [history, idTokenEntityId, setEntity])

  const logout = useCallback(() => {
    history.push("/logout")
  }, [history])

  const openUserManagementModal = useCallback(() => {
    history.push("/profile")
  }, [history])

  const openUserSettings = useCallback(() => {
    if (theme !== KR_THEME.theme) {
      history.push(`/${theme}/user-preferences`)
    } else {
      history.push("/user-settings/general")
    }
  }, [history])

  const openLegal = useCallback(() => {
    history.push("/legal")
  }, [history])

  const openChangePassword = useCallback(() => {
    history.push("/profile/change_password")
  }, [history])

  const toggleCollapse = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen)
  }, [])

  const toggleSidebar = useCallback(() => {
    // toggle function can be called even for legacy sidebar, since the provider is still there,
    //  but the sidebar is not rendered so it will not affect the legacy sidebar
    toggleShadcnSidebar()
    if (isSidebarOpen) {
      closeSidebar()
    } else {
      openSidebar()
    }
  }, [isSidebarOpen, closeSidebar, openSidebar, state])

  const hub = theme === KR_THEME.theme
  const renderSettings = hub || modulesReady.includes(theme)
  const isLoginPath = history.location.pathname.includes("/login")
  const isProfileIconDropdownPath =
    history.location.pathname.includes("/profile") ||
    history.location.pathname.includes("/user-settings") ||
    history.location.pathname.includes("/legal")

  if (isLoginPath) return null

  return (
    <header className={`${className} z-50`}>
      <div className="container navbar-container">
        <div className="flex navbar-row justify-between">
          <div className="flex items-center px-3">
            {hub && !!loggedIn && !isProfileIconDropdownPath && (
              <Button
                variant="ghost"
                size="icon"
                className="mx-3 [&_svg]:size-8"
                onClick={toggleSidebar}
                data-testid="navbar-collapse-toggle"
                aria-label="navbar collapse button"
              >
                <Menu />
              </Button>
            )}
            <DynamicTitle history={history} />
          </div>

          <div className="flex items-center items-center w-full px-3">
            <NavbarElements />
            {idTokenEntityId && (
              <EntityDropdown
                history={history}
                handleChange={handleEntityChange}
                entities={entities}
                entity_id={entity_id}
                loadIAM={loadIAM}
              />
            )}
          </div>
          <Link
            aria-label="Home Page"
            to="/home"
            className="mr-4 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring w-[124px] h-full rounded-md flex items-center"
          >
            <div className="navbar-company-logo-styling">
              <div id="logo" style={{ width: "128px" }} />
            </div>
          </Link>
          {loggedIn && (
            <div className="mr-4">
              <DropdownMenu open={isOpen} onOpenChange={toggleCollapse}>
                <DropdownMenuTrigger className="top-nav-profile-btn" asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                    data-testid="navbar-profile-button"
                  >
                    <AvatarComponent
                      firstName={firstName}
                      lastName={lastName}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="mr-2 w-[216px] p-0">
                  {/* User info section */}
                  <div className="py-4 px-3 border-b flex items-center border-b-gray-200">
                    <AvatarComponent
                      firstName={firstName}
                      lastName={lastName}
                    />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-foreground">
                        {firstName} {lastName}
                      </h3>
                      <p className="text-muted-foreground text-xs">{role}</p>
                    </div>
                  </div>
                  <DropdownMenuItem
                    data-testid="navbar-profile-dropdown-menu-item"
                    onClick={openUserManagementModal}
                    className="px-3 py-2 cursor-pointer border-gray-200 rounded-none text-[14px] text-foreground"
                  >
                    Manage Profile
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={openChangePassword}
                    className="px-3 py-2 cursor-pointer  rounded-none text-[14px] text-foreground"
                  >
                    Change Password
                  </DropdownMenuItem>

                  {renderSettings && (
                    <DropdownMenuItem
                      data-testid="navbar-profile-dropdown-menu-item"
                      onClick={openUserSettings}
                      className="px-3 py-2 cursor-pointer rounded-none text-[14px] text-foreground"
                    >
                      User Preferences
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    data-testid="navbar-profile-dropdown-menu-item"
                    onClick={openLegal}
                    className="px-3 py-2 cursor-pointer border-t border-gray-200 rounded-none text-[14px] text-foreground"
                  >
                    Legal
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    data-testid="navbar-profile-dropdown-menu-item"
                    onClick={logout}
                    className="px-3 py-2 cursor-pointer border-t border-gray-200 rounded-none text-[14px]"
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

NavbarComponent.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  className: PropTypes.string.isRequired,
  NavbarElements: PropTypes.func.isRequired,
  setEntities: PropTypes.func.isRequired,
  setEntity: PropTypes.func.isRequired,
  entity_id: PropTypes.string,
  idTokenEntityId: PropTypes.string,
  openSidebar: PropTypes.func.isRequired,
  closeSidebar: PropTypes.func.isRequired,
  isSidebarOpen: PropTypes.bool,
  role: PropTypes.string,
}

NavbarComponent.defaultProps = {
  firstName: undefined,
  lastName: undefined,
  entities: [],
  isSidebarOpen: false,
  entity_id: undefined,
  idTokenEntityId: undefined,
  role: undefined,
}

export default NavbarComponent
